import defaultTheme from 'tailwindcss/defaultTheme';

/**
 * Convert Tailwind rem-based breakpoint to pixels
 * Tailwind uses 16px as the default root font size (1rem = 16px)
 */
function remToPx(remValue: string): number {
  return parseFloat(remValue) * 16;
}

/**
 * Generate responsive image sizes string using Tailwind breakpoints
 *
 * @param breakpoints Array of [breakpoint, size] tuples, plus a default size
 * @returns CSS sizes string for Next.js Image component
 *
 * @example
 * // "(max-width: 1280px) 100vw, (max-width: 1535px) 50vw, 33vw"
 * getImageSizes([
 *   ['xl', '100vw'],
 *   ['2xl', '50vw']
 * ], '33vw')
 */
export function getImageSizes(
  breakpoints: Array<[keyof typeof defaultTheme.screens, string]>,
  defaultSize: string
): string {
  const mediaQueries = breakpoints.map(([breakpoint, size], index) => {
    const isLast = index === breakpoints.length - 1;
    const breakpointPx = remToPx(defaultTheme.screens[breakpoint]);
    const maxWidth = isLast ? breakpointPx - 1 : breakpointPx;

    return `(max-width: ${maxWidth}px) ${size}`;
  });

  return [...mediaQueries, defaultSize].join(', ');
}

/**
 * Get pixel value for a Tailwind breakpoint
 *
 * @param breakpoint Tailwind screen breakpoint key
 * @returns Pixel value as number
 *
 * @example
 * getBreakpointPx('xl') // 1280
 */
export function getBreakpointPx(
  breakpoint: keyof typeof defaultTheme.screens
): number {
  return remToPx(defaultTheme.screens[breakpoint]);
}
